/**
 * Import jízdních řádů CELÉ ČR z agregovaného datasetu
 * tangero/jizdni-rady-czech-republic (CC BY 4.0; 215 dopravců, MHD
 * i regionální doprava; zdrojově KOMPLET dat CHAPS/celostátní řády).
 *
 * Dataset je publikovaný jako jednotlivé GTFS soubory na GitHubu
 * (raw.githubusercontent posílá CORS, takže funguje bez proxy);
 * obří stop_times.txt.gz se STREAMUJE (gunzip po kusech) a rovnou
 * ořezává na bounding box trasy — nikdy se nedrží celý v paměti.
 * Výsledek jde přes existující parseGtfsZip (mini-zip bez komprese).
 */
import { Gunzip, strToU8, zipSync } from 'fflate';
import { parseGtfsZip, type GtfsImportResult } from './gtfs';
import { splitCsvLine, type GtfsBbox } from './gtfsLarge';

const CZ_BASE = 'https://raw.githubusercontent.com/tangero/jizdni-rady-czech-republic/main/data/merged/';

export const CZ_ATTRIBUTION =
  'Data: tangero/jizdni-rady-czech-republic (CC BY 4.0), agregované celostátní jízdní řády.';

const fmtMB = (n: number): string => `${(n / 1048576).toFixed(1)} MB`;

async function fetchStream(
  url: string,
  label: string,
  onProgress: ((t: string) => void) | undefined,
  onChunk: (chunk: Uint8Array, final: boolean) => void,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: '*/*' } });
  } catch {
    throw new Error(`Stažení „${label}" selhalo — zkontrolujte připojení.`);
  }
  if (!res.ok) throw new Error(`Stažení „${label}" selhalo (HTTP ${res.status}).`);
  if (!res.body) {
    onChunk(new Uint8Array(await res.arrayBuffer()), true);
    return;
  }
  const reader = res.body.getReader();
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    loaded += value.length;
    onProgress?.(`Stahuji ${label}… ${fmtMB(loaded)}`);
    onChunk(value, false);
  }
  onChunk(new Uint8Array(0), true);
}

/** Stáhne textový soubor (s průběhem) jako string. */
async function fetchText(url: string, label: string, onProgress?: (t: string) => void): Promise<string> {
  const chunks: Uint8Array[] = [];
  await fetchStream(url, label, onProgress, (c) => {
    if (c.length) chunks.push(c);
  });
  let len = 0;
  for (const c of chunks) len += c.length;
  const all = new Uint8Array(len);
  let off = 0;
  for (const c of chunks) {
    all.set(c, off);
    off += c.length;
  }
  return new TextDecoder().decode(all);
}

/** Validátor aktuálnosti datasetu (ETag největšího souboru). */
export async function czEtag(): Promise<string | null> {
  try {
    const res = await fetch(CZ_BASE + 'stop_times.txt.gz', { method: 'HEAD' });
    return res.ok ? res.headers.get('ETag') : null;
  } catch {
    return null;
  }
}

export async function importCzMerged(opts: {
  bbox: GtfsBbox;
  serviceDate: string; // YYYY-MM-DD
  maxLines?: number;
  onProgress?: (text: string) => void;
}): Promise<GtfsImportResult> {
  const { bbox, onProgress } = opts;

  // 1) Zastávky → množina id v oblasti (a rovnou zmenšený stops.txt).
  const stopsText = await fetchText(CZ_BASE + 'stops.txt', 'zastávky ČR', onProgress);
  const stopLines = stopsText.split(/\r?\n/);
  const stopHeader = (stopLines[0] ?? '').replace(/^﻿/, '');
  const sCols = splitCsvLine(stopHeader);
  const iId = sCols.indexOf('stop_id');
  const iLat = sCols.indexOf('stop_lat');
  const iLng = sCols.indexOf('stop_lon');
  if (iId < 0 || iLat < 0 || iLng < 0) throw new Error('Dataset ČR: stops.txt nemá očekávané sloupce.');
  const inBox = new Set<string>();
  const keptStops: string[] = [stopHeader];
  for (let i = 1; i < stopLines.length; i++) {
    const line = stopLines[i];
    if (!line) continue;
    const row = line.includes('"') ? splitCsvLine(line) : line.split(',');
    const lat = Number(row[iLat]);
    const lng = Number(row[iLng]);
    if (lat >= bbox.south && lat <= bbox.north && lng >= bbox.west && lng <= bbox.east) {
      inBox.add(row[iId]);
      keptStops.push(line);
    }
  }
  if (inBox.size === 0) {
    throw new Error('V okolí vaší trasy nejsou v celostátním datasetu žádné zastávky.');
  }

  // 2) stop_times.txt.gz — streamovaný gunzip + filtr řádků podle zastávek.
  onProgress?.('Stahuji jízdní časy ČR…');
  const keptTimes: string[] = [];
  const tripIds = new Set<string>();
  let timesHeader = '';
  let stIdIdx = -1;
  let stTripIdx = -1;
  let carry = '';
  const decoder = new TextDecoder();
  const handleLine = (line: string): void => {
    if (!timesHeader) {
      timesHeader = line.replace(/^﻿/, '');
      const cols = splitCsvLine(timesHeader);
      stIdIdx = cols.indexOf('stop_id');
      stTripIdx = cols.indexOf('trip_id');
      if (stIdIdx < 0 || stTripIdx < 0) throw new Error('Dataset ČR: stop_times nemá očekávané sloupce.');
      return;
    }
    if (!line) return;
    const cols = line.includes('"') ? splitCsvLine(line) : line.split(',');
    if (inBox.has(cols[stIdIdx])) {
      keptTimes.push(line);
      tripIds.add(cols[stTripIdx]);
    }
  };
  const gz = new Gunzip((data, final) => {
    carry += decoder.decode(data, { stream: !final });
    let nl = carry.indexOf('\n');
    while (nl >= 0) {
      handleLine(carry.slice(0, nl).replace(/\r$/, ''));
      carry = carry.slice(nl + 1);
      nl = carry.indexOf('\n');
    }
    if (final && carry.trim()) handleLine(carry.replace(/\r$/, ''));
  });
  await fetchStream(CZ_BASE + 'stop_times.txt.gz', 'jízdní časy ČR', onProgress, (chunk, final) => {
    if (chunk.length) gz.push(chunk, false);
    if (final) gz.push(new Uint8Array(0), true);
  });

  // 3) Kalendář výjimek: služby aktivní pro zvolený den (dataset používá
  // calendar_dates). Soubor je volitelný — bez něj bereme všechny spoje.
  onProgress?.('Zpracovávám kalendář a spoje…');
  const ymd = opts.serviceDate.replace(/-/g, '');
  let activeServices: Set<string> | null = null;
  try {
    const calText = await fetchText(CZ_BASE + 'calendar_dates.txt', 'kalendář ČR', onProgress);
    const calLines = calText.split(/\r?\n/);
    const cCols = splitCsvLine((calLines[0] ?? '').replace(/^﻿/, ''));
    const cSid = cCols.indexOf('service_id');
    const cDate = cCols.indexOf('date');
    const cType = cCols.indexOf('exception_type');
    if (cSid >= 0 && cDate >= 0) {
      activeServices = new Set<string>();
      for (let i = 1; i < calLines.length; i++) {
        const line = calLines[i];
        if (!line) continue;
        const row = line.includes('"') ? splitCsvLine(line) : line.split(',');
        if (row[cDate] === ymd && (cType < 0 || row[cType] !== '2')) activeServices.add(row[cSid]);
      }
      if (activeServices.size === 0) activeServices = null; // den mimo rozsah → vše
    }
  } catch {
    activeServices = null;
  }

  // 4) Spoje: jen ty projíždějící oblastí a jedoucí ve zvolený den.
  const tripsText = await fetchText(CZ_BASE + 'trips.txt', 'spoje ČR', onProgress);
  const tripLines = tripsText.split(/\r?\n/);
  const tHeader = (tripLines[0] ?? '').replace(/^﻿/, '');
  const tCols = splitCsvLine(tHeader);
  const tTrip = tCols.indexOf('trip_id');
  const tRoute = tCols.indexOf('route_id');
  const tService = tCols.indexOf('service_id');
  const collectTrips = (services: Set<string> | null): { lines: string[]; routes: Set<string> } => {
    const lines: string[] = [tHeader];
    const routes = new Set<string>();
    for (let i = 1; i < tripLines.length; i++) {
      const line = tripLines[i];
      if (!line) continue;
      const row = line.includes('"') ? splitCsvLine(line) : line.split(',');
      if (!tripIds.has(row[tTrip])) continue;
      if (services && tService >= 0 && !services.has(row[tService])) continue;
      lines.push(line);
      routes.add(row[tRoute]);
    }
    return { lines, routes };
  };
  let calendarFallback = false;
  let picked = collectTrips(activeServices);
  if (activeServices) {
    const all = collectTrips(null);
    // Kalendář datasetu nepokrývá zvolený den dostatečně → vezmeme vše,
    // jinak by oblast vypadala téměř bez dopravy. (Jen u větších sítí —
    // u pár spojů může být řídkost legitimní.)
    const pickedN = picked.lines.length - 1;
    const allN = all.lines.length - 1;
    if (allN >= 25 && pickedN < allN * 0.2) {
      picked = all;
      calendarFallback = true;
    }
  }
  const keptTrips = picked.lines;
  const routeIds = picked.routes;

  // 5) Linky v oblasti.
  const routesText = await fetchText(CZ_BASE + 'routes.txt', 'linky ČR', onProgress);
  const routeLines = routesText.split(/\r?\n/);
  const rHeader = (routeLines[0] ?? '').replace(/^﻿/, '');
  const rCols = splitCsvLine(rHeader);
  const rId = rCols.indexOf('route_id');
  const keptRoutes: string[] = [rHeader];
  for (let i = 1; i < routeLines.length; i++) {
    const line = routeLines[i];
    if (!line) continue;
    const row = line.includes('"') ? splitCsvLine(line) : line.split(',');
    if (routeIds.has(row[rId])) keptRoutes.push(line);
  }

  // 6) Mini-zip (bez kalendáře — spoje už jsou vyfiltrované na den) → parser.
  onProgress?.('Sestavuji síť…');
  const mini = zipSync(
    {
      'stops.txt': strToU8(keptStops.join('\n') + '\n'),
      'routes.txt': strToU8(keptRoutes.join('\n') + '\n'),
      'trips.txt': strToU8(keptTrips.join('\n') + '\n'),
      'stop_times.txt': strToU8(timesHeader + '\n' + keptTimes.join('\n') + '\n'),
    },
    { level: 0 },
  );
  const result = parseGtfsZip(mini, { serviceDate: opts.serviceDate, maxLines: opts.maxLines ?? 500 });
  result.feedName = result.feedName ?? 'Jízdní řády ČR';
  if (calendarFallback) {
    result.warnings.unshift(
      'Kalendář datasetu nepokrývá zvolený den — použity všechny spoje (odjezdy mohou míchat všední dny a víkend).',
    );
  }
  result.warnings.unshift(
    `Celostátní dataset ořezán na okolí vaší trasy (${inBox.size} zastávek v oblasti). ${CZ_ATTRIBUTION}`,
  );
  return result;
}
